import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  FileText,
  Clock,
  Eye,
  Users,
  ExternalLink
} from 'lucide-react';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';
import { usePublicGeneration } from '@/hooks/useGenerations';
import { Button } from '@/components/ui/button';

const PublicGeneration: React.FC = () => {
  const { slug, lang } = useParams<{ slug?: string; lang?: string }>();
  const { data: generation, isLoading, error } = usePublicGeneration(slug!);
  const prefix = `/${lang || 'en'}`;

  if (isLoading) {
    return (
      <AppLayout showLanguageSwitcher={false}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      </AppLayout>
    );
  }

  if (error || !generation) {
    return (
      <AppLayout showLanguageSwitcher={false}>
        <AppHeader
          title="Generation Not Found"
          subtitle="The shared generation you're looking for doesn't exist or has been removed."
        />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="w-16 h-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Generation Not Found</h3>
              <p className="text-muted-foreground text-center mb-4">
                This shared generation may have been removed or made private.
              </p>
              <Button asChild>
                <Link to={prefix} className="inline-flex items-center">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Visit Homepage
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const questions = generation.generated_content?.questions || [];

  return (
    <AppLayout showLanguageSwitcher={false}>
      <AppHeader
        title={generation.title || 'Shared Generation'}
        subtitle="Shared survey generation"
      />

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Generation Info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Generation Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{generation.generation_type}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium">{generation.estimated_duration_minutes || 'N/A'} min</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Views</p>
                  <p className="font-medium">{generation.view_count}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-sm text-muted-foreground">Shares</p>
                  <p className="font-medium">{generation.share_count}</p>
                </div>
              </div>
            </div>
            <Separator className="my-4" />
            <div className="flex gap-2">
              <Badge variant="outline">{generation.status}</Badge>
              <Badge variant="outline">Version {generation.version}</Badge>
              <Badge variant="outline">{questions.length} questions</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card>
          <CardHeader>
            <CardTitle>Survey Questions ({questions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {questions.map((question: any, index: number) => (
                <div key={question.id || index} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-medium">Question {index + 1}</h3>
                    <Badge variant="outline" className="text-xs">
                      {question.type}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {question.question}
                  </p>
                  
                  {question.type === 'multiple_choice' && question.options && (
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground">Options:</p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        {question.options.map((option: string, optionIndex: number) => (
                          <li key={optionIndex} className="text-muted-foreground">
                            {option}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
              
              {questions.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No questions found in this generation.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="mt-6">
          <CardContent className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              Want to create your own surveys? 
            </p>
            <Button asChild>
              <Link to={prefix} className="inline-flex items-center">
                <ExternalLink className="w-4 h-4 mr-2" />
                Get Started for Free
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </AppLayout>
  );
};

export default PublicGeneration;